import {
  BASE_LANGUAGES,
  isPortalLanguage,
  LanguageMap,
  LANGUAGES,
  PortalLanguage,
} from "common/i18nConstants";
import { capitalize } from "common/utils/strings";
import { mapValues } from "common/utils/objects";
import type { i18n, TFunction } from "i18next";

/**
 * a common handler for requests for localizing missing keys in the localization
 * database.  pass as the option of the same name to `i18next.init`.
 */
export const missingKeyHandler: Parameters<
  i18n["init"]
>[0]["missingKeyHandler"] = (language, namespace, key, fallbackValue) => {
  throw new Error(`Key not found ${key}, ${language}, ${fallbackValue}`);
};

/**
 * a handler for i18next's "languageChanged" event that intercepts change events
 * to unsupported languages and attempts to redirect to a near match.
 * @param i18n the i18next object
 * @param lng the locale of the language that has been changed to
 */
export function changeToClosestPortalLang(i18n: i18n, lng: string): void {
  if (isPortalLanguage(lng)) {
    return;
  }
  const closestPortalLang = resolveToClosestPortalLang(lng);
  if (closestPortalLang !== undefined) {
    i18n.changeLanguage(closestPortalLang);
  }
}

export const setLocale = (i18n: i18n, locale: PortalLanguage): void => {
  const documentElement = globalThis.document.documentElement;
  documentElement.lang = locale;

  if (locale !== i18n.language) {
    i18n.changeLanguage(locale);
  }
};

// We use the browser's Intl.DisplayNames to render names of languages, but we
// want to use simpler names e.g. "English" instead of "American English", so
// use `BASE_LANGUAGES` rather than the full locales.
const displayNames: LanguageMap<string> = mapValues(
  BASE_LANGUAGES,
  (language) =>
    capitalize(
      // We want the name "Serbian" to use the latin character set, but we do not need it to say "Serbian (Latin)" - so, special case it
      language === "sr"
        ? new Intl.DisplayNames(["sr-Latn"], { type: "language" }).of("sr")
        : new Intl.DisplayNames([language], { type: "language" }).of(language)
    )
);

export const toDisplayName = (language: PortalLanguage): string =>
  displayNames[language];

export const resolveToClosestPortalLang = (
  langCode: string | undefined
): PortalLanguage | undefined => {
  if (!langCode) {
    return;
  }
  if (isPortalLanguage(langCode)) {
    return langCode;
  }
  return LANGUAGES.find((pl) => pl.slice(0, 2) === langCode.slice(0, 2));
};

// Returns a translated string if it exists in the current language or fallback
// language, or returns `undefined` if the string does not exist at all. This
// differs from `t(i18nKey)` because that returns the translation key itself if
// the string does not exist.
export const translateIfKeyExists = (
  t: TFunction,
  options: { i18nKey: string }
): string | undefined => {
  // Use null as a sentinel value (can't use "" since we define
  // `returnEmptyString: false` globally).
  // eslint-disable-next-line unicorn/no-null
  const result = t(options.i18nKey, { defaultValue: null });
  return result || undefined;
};
